import type {
  ReplayBundle,
  ReplayFix,
  ReplayGameKind,
  ReplayGamePin,
  GeoArea,
  GeoLineKind,
  GeoLineStyle,
  ReplayParticipant,
  ReplayRole,
} from '@wehunt/ui'
import { argbToCss, paletteColor } from './replayColors'
import type {
  HuntReplayDto,
  HuntReplayHuntingGroundDto,
  HuntReplayParticipantDto,
} from './huntReplayDto'

export interface ReplayBundleContext {
  /** The archive carries the hunt, not the team it belonged to. */
  teamName?: string
}

/**
 * Maps the API response onto the viewer's own shape.
 *
 * The cheapest, highest-leverage thing in this feature to test: pure, synchronous,
 * and the single place where a wire change can break the replay.
 */
export function replayDtoToBundle(
  dto: HuntReplayDto,
  context: ReplayBundleContext = {},
): ReplayBundle {
  const trailsByCarrier = new Map(dto.trails.map((trail) => [trail.carrierId, trail.points]))

  // Mobile drops a carrier whose role it does not know rather than guessing
  // "hunter". Filtering before mapping means palette colours are assigned over
  // the carriers that are actually drawn, and their trails go with them.
  const participants = dto.participants
    .filter((participant) => participant.role === 'dog' || participant.role === 'hunter')
    .map((participant, index) =>
      toParticipant(participant, index, trailsByCarrier.get(participant.carrierId) ?? []),
    )

  return {
    huntId: dto.huntId,
    name: dto.name ?? undefined,
    teamName: context.teamName ?? dto.groupName ?? undefined,
    startsAt: Date.parse(dto.startsAt),
    endsAt: Date.parse(dto.endsAt),
    participants,
    // Called through an arrow rather than passed by reference: `map` also hands the
    // callback the array, so a reference silently binds any third parameter a future
    // signature grows. The two arguments this needs are named here instead.
    gamePins: dto.gamePins.map((pin, index) => toGamePin(pin, index)),
    // A pin with no type has no symbol to draw, so it is not carried at all.
    mapPins: dto.mapPins.flatMap((pin, index) =>
      pin.pinType
        ? [
            {
              id: pin.id ?? `map-pin-${index}`,
              name: pin.name ?? undefined,
              pinType: pin.pinType,
              ...(pin.colorArgb == null ? {} : { color: argbToCss(pin.colorArgb) }),
              ...(pin.customSymbol ? { customSymbol: pin.customSymbol } : {}),
              ...(pin.description ? { description: pin.description } : {}),
              lat: pin.lat,
              lon: pin.lon,
            },
          ]
        : [],
    ),
    // A line with no id is dropped, as on mobile.
    lines: dto.lines.flatMap((line) =>
      line.id
        ? [
            {
              id: line.id,
              name: line.name ?? undefined,
              color: argbToCss(line.colorArgb),
              width: line.width,
              style: toLineStyle(line.style),
              kind: toLineKind(line.pinType),
              points: line.points,
            },
          ]
        : [],
    ),
    ...groundsByType(dto.huntingGrounds),
  }
}

/**
 * The hunting grounds, split by area type into the three lists the viewer
 * draws - the split Clubhouse's team maps use. Undefined (not empty) when the
 * payload carries no grounds field at all, so an older API reads as "no
 * ground" rather than "a ground with nothing in it".
 */
function groundsByType(grounds: HuntReplayHuntingGroundDto[] | undefined) {
  if (!grounds) {
    return { boundaryAreas: undefined, subAreaPolygons: undefined, forbiddenPolygons: undefined }
  }
  const of = (type: HuntReplayHuntingGroundDto['areaType']) =>
    grounds
      .filter((g) => g.areaType === type)
      .map(
        (g, index): GeoArea => ({
          id: g.id ?? `replay-area-${type}-${index}`,
          name: g.name ?? '',
          rings: g.rings.map(openRing),
          outerCount: g.outerRingCount,
          ...(g.colorArgb == null ? {} : { color: argbToCss(g.colorArgb) }),
          ...(g.areaSource ? { areaSource: g.areaSource } : {}),
        }),
      )
  return { boundaryAreas: of('hunt'), subAreaPolygons: of('sub'), forbiddenPolygons: of('forbidden') }
}

/**
 * A GeoJSON ring repeats its first point at the end; the team-map renderer
 * closes rings itself and draws the repeat as a zero-length edge. The same
 * trim Clubhouse applies to the grounds it reads.
 */
function openRing(ring: { lat: number; lon: number }[]) {
  const first = ring[0]
  let end = ring.length
  while (end > 1 && ring[end - 1].lat === first.lat && ring[end - 1].lon === first.lon) end--
  return end === ring.length ? ring : ring.slice(0, end)
}

function toParticipant(
  dto: HuntReplayParticipantDto,
  index: number,
  points: HuntReplayDto['trails'][number]['points'],
): ReplayParticipant {
  const role: ReplayRole = dto.role === 'dog' ? 'dog' : 'hunter'
  const anonymous = dto.anonymous === true

  const fixes: ReplayFix[] = points.map((point) => ({
    t: point.t,
    lat: point.lat,
    lon: point.lon,
    heading: point.heading ?? undefined,
    speed: point.speed ?? undefined,
    barking: point.barking === true,
  }))

  return {
    id: dto.carrierId,
    role,
    // An anonymous carrier's names are dropped even if the API sent one: the
    // person asked not to be named, and this is the last place that can refuse.
    name: anonymous ? '' : (dto.displayName ?? ''),
    color: dto.colorArgb != null ? argbToCss(dto.colorArgb) : paletteColor(index),
    windowStart: Date.parse(dto.windowStart),
    windowEnd: Date.parse(dto.windowEnd),
    ownerName: anonymous ? undefined : (dto.ownerName ?? undefined),
    anonymous,
    // The API says who the caller is. Comparing a carrier id with the signed-in
    // uid would be wrong in a shared replay, where a hunter's id is a per-share
    // pseudonym. Only a hunter can be the viewer.
    isMe: role === 'hunter' && dto.isYou === true,
    fixes,
  }
}

function toGamePin(dto: HuntReplayDto['gamePins'][number], index: number): ReplayGamePin {
  return {
    // A pin with no id still has to be a stable React key, and its position in the
    // archive is stable for a hunt that has already ended.
    id: dto.id ?? `game-pin-${index}`,
    kind: toGameKind(dto.kind),
    gameNames: dto.gameNames,
    lat: dto.lat,
    lon: dto.lon,
    at: Date.parse(dto.at),
  }
}

/**
 * The same rule as the mobile share mapper (`_style` in campfire's
 * `replay_share_mapping.dart`): the API sends one spelling per style, and
 * anything else — an unknown value, a missing one, even the archive's own
 * `dash_dot` — is drawn solid, so a line looks the same on both clients.
 * `dashDot` is respelled because the web line renderer keys its layers on
 * `dash_dot`.
 */
function toLineStyle(wire: string | null | undefined): GeoLineStyle {
  switch (wire) {
    case 'dashed':
      return 'dashed'
    case 'dotted':
      return 'dotted'
    case 'dashDot':
      return 'dash_dot'
    default:
      return 'solid'
  }
}

/**
 * The mobile share mapper's rule (`ReplayLinePinType.fromString` falling back
 * to `line`): an unknown or missing kind is a plain line. The API resolves the
 * archive's cap-inferred arrows before sending, so no cap inference here.
 */
function toLineKind(wire: string | null | undefined): GeoLineKind {
  switch (wire) {
    case 'arrow':
    case 'trail':
      return wire
    default:
      return 'line'
  }
}

function toGameKind(kind: string): ReplayGameKind {
  switch (kind) {
    case 'downed':
      return 'downed'
    case 'wounded':
      return 'wounded'
    // Anything unrecognised is an observation, matching how the archive's own
    // writers treated a missing kind.
    default:
      return 'observed'
  }
}
